import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";
import PaymentListDetail from "./PaymentListDetail";
import { DownloadListImageButton } from "./shared/DownloadListImageButton";

const CREATE_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

// Payment Lists (RTGS/NEFT) — the batch containers a creator builds up with
// lines and submits as a whole. All four roles can view; only
// Director/Manager/Back Office can create.
export default function PaymentListsTab() {
  const { user } = useAuth();
  const canCreate = CREATE_ROLES.includes(user?.role);
  const refData = usePaymentDeskRefData();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mineOnly, setMineOnly] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [creating, setCreating] = useState(false);

  const load = (pageNum = 1) => {
    setLoading(true);
    client
      .get("/payment-desk/payment-lists", { params: { mine: mineOnly, page: pageNum, page_size: 50 } })
      .then((res) => {
        setLists(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [mineOnly]);

  const createList = async () => {
    setCreating(true);
    try {
      const res = await client.post("/payment-desk/payment-lists");
      toast.success(`Payment List created — ${res.data.list_id}`);
      setSelectedId(res.data.list_id);
      load(page);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-lists-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          data-testid="payment-list-mine-toggle"
          onClick={() => setMineOnly((v) => !v)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            mineOnly ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {mineOnly ? "My Lists" : "All Lists"}
        </button>
        {canCreate && (
          <Button data-testid="payment-list-add-button" onClick={createList} disabled={creating} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> New Payment List
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="payment-list-table">
          <TableHeader>
            <TableRow>
              <TableHead>List ID</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Lines</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Image</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : lists.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No payment lists found.</TableCell></TableRow>
            ) : (
              lists.map((l) => (
                <TableRow key={l.list_id} data-testid={`payment-list-row-${l.list_id}`} className="cursor-pointer" onClick={() => setSelectedId(l.list_id)}>
                  <TableCell className="font-mono text-xs font-semibold">{l.list_id}</TableCell>
                  <TableCell className="text-xs">{l.created_by_name}</TableCell>
                  <TableCell>{l.line_count}</TableCell>
                  <TableCell>{money(l.total_amount)}</TableCell>
                  <TableCell>
                    <Badge className={l.submitted ? "bg-emerald-600" : "bg-slate-400"}>{l.submitted ? "Submitted" : "Draft"}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-right"><DownloadListImageButton listId={l.list_id} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="payment-list-pagination" />

      {selectedId && (
        <PaymentListDetail
          listId={selectedId}
          refData={refData}
          onClose={() => setSelectedId(null)}
          onChanged={() => load(page)}
        />
      )}
    </div>
  );
}
